import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@web-frontend/shared/services/toast/toast.service';

export interface FileValidationOptions {
  maxSizeBytes?: number;
  acceptedTypes?: string[];
  showErrorMessages?: boolean;
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FileTypeValidationOptions {
  acceptedTypes: string[];
  onError?: (error: string) => void;
}

export interface FileExtensionValidationOptions {
  onError?: (fileName: string) => void;
}

export interface FileSizeValidationOptions {
  maxSizeBytes: number;
  onError?: (fileName: string) => void;
}

@Injectable({
  providedIn: 'root',
})
export class FileValidationService {
  constructor(
    private toastService: ToastService,
    private i18n: TranslateService
  ) {}

  validateFile(
    file: File,
    options: FileValidationOptions = {}
  ): FileValidationResult {
    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }

    const {
      maxSizeBytes = 2 * 1024 * 1024,
      acceptedTypes = [],
      showErrorMessages = true,
    } = options;

    if (acceptedTypes.length > 0) {
      const normalizedAcceptedTypes = acceptedTypes.map((type) => type.trim());

      const isValidType = normalizedAcceptedTypes.some((acceptedType) => {
        if (acceptedType.startsWith('.')) {
          return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
        } else {
          return file.type === acceptedType;
        }
      });

      if (!isValidType) {
        const errorMessage = 'fileUpload.formatError';
        if (showErrorMessages) {
          this.showErrorToast(errorMessage);
        }
        return { isValid: false, error: errorMessage };
      }
    }

    if (file.size > maxSizeBytes) {
      const errorMessage = 'fileUpload.errorUploadLimitFormat';
      if (showErrorMessages) {
        this.showErrorToast(errorMessage);
      }
      return { isValid: false, error: errorMessage };
    }

    return { isValid: true };
  }

  validateFileFromInput(
    file: File,
    inputElement: HTMLInputElement | undefined,
    options: Omit<FileValidationOptions, 'acceptedTypes'> = {}
  ): FileValidationResult {
    const acceptedTypes =
      inputElement?.accept?.split(',').map((type) => type.trim()) || [];

    return this.validateFile(file, {
      ...options,
      acceptedTypes,
    });
  }

  validateFileList(
    files: FileList,
    inputElement: HTMLInputElement | undefined,
    options: Omit<FileValidationOptions, 'acceptedTypes'> = {}
  ): FileValidationResult {
    const file = files.item(0);
    if (!file) {
      return { isValid: false, error: 'No file selected' };
    }

    return this.validateFileFromInput(file, inputElement, options);
  }

  validateFileType(file: File, options: FileTypeValidationOptions): boolean {
    const isValid = options.acceptedTypes.includes(file.type);
    if (!isValid && options.onError) {
      options.onError(file.type);
    }
    return isValid;
  }

  validateFileExtension(
    file: File,
    options: FileExtensionValidationOptions = {}
  ): boolean {
    const fileSplit = file.name.split('.');
    const isValid = fileSplit.length > 1;
    if (!isValid && options.onError) {
      options.onError(file.name);
    }
    return isValid;
  }

  validateFileSize(file: File, options: FileSizeValidationOptions): boolean {
    const isValid = file.size <= options.maxSizeBytes;
    if (!isValid && options.onError) {
      options.onError(file.name);
    }
    return isValid;
  }

  private showErrorToast(messageKey: string): void {
    this.toastService.show({
      text: this.i18n.instant(messageKey),
      type: 'error',
      msDuration: 4000,
    });
  }
}
